// React imports
import React, { useEffect, useRef, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { generateKRAReports, allKRAAssignmentsUrl } from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
} from "../utils";
import { Assignment } from "./Types/KRAAssignmentsTypes";

const KRAAssignments: React.FC = () => {
  // useState declarations
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  const [designationFilter, setDesignationFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  // Status counts (constant across all pages, calculated from full dataset on backend)
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    accepted: 0,
    awaiting_manager_review: 0,
    awaiting_hr_review: 0,
    completed: 0,
  });
  const [filterOptions, setFilterOptions] = useState<{
    employees: Array<{ employee_code: string; employee_name: string; employee_email: string }>;
    designations: string[];
    templates: string[];
  }>({
    employees: [],
    designations: [],
    templates: []
  });

  // Other constants and hooks
  const showToast = useCustomToast();

  // Redux variables
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isFirstFetch = useRef(true);

  const fetchAssignments = async () => {
    if (isFirstFetch.current) {
      setInitialLoading(true);
    } else {
      setLoading(true);
    }
    try {
      // Build URL with pagination and filter parameters
      let url = `${allKRAAssignmentsUrl}?page=${currentPage}&page_size=${pageSize}`;
      
      // Add filter parameters
      if (employeeFilter !== "all") {
        url += `&employee_code=${encodeURIComponent(employeeFilter)}`;
      }
      if (designationFilter !== "all") {
        url += `&designation=${encodeURIComponent(designationFilter)}`;
      }
      if (statusFilter !== "all") {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }
      if (templateFilter !== "all") {
        url += `&template=${encodeURIComponent(templateFilter)}`;
      }
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      
      // Add sorting parameters
      if (sortField) {
        url += `&ordering=${sortOrder === "desc" ? "-" : ""}${sortField}`;
      }
      
      const response = await dispatch(fetchData(url));
      
      // Handle paginated response
      if (response && typeof response === 'object' && !Array.isArray(response) && 'results' in response) {
        const paginatedResponse = response as {
          results: unknown[];
          total_pages?: number;
          count?: number;
          status_counts?: Record<string, number>;
          filter_options?: unknown;
        };
        setAssignments(paginatedResponse.results as Assignment[]);
        setTotalPages(paginatedResponse.total_pages || 1);
        setTotalCount(paginatedResponse.count || 0);
        
        // Store status counts from backend (constant across all pages)
        if (paginatedResponse.status_counts) {
          setStatusCounts(paginatedResponse.status_counts as {
            pending: number;
            accepted: number;
            awaiting_manager_review: number;
            awaiting_hr_review: number;
            completed: number;
          });
        }
        
        // Store filter options from backend (constant across all pages)
        if (paginatedResponse.filter_options) {
          setFilterOptions(paginatedResponse.filter_options as {
            employees: Array<{ employee_code: string; employee_name: string; employee_email: string }>;
            designations: string[];
            templates: string[];
          });
        }
        
      } else if (response && Array.isArray(response)) {
        // Fallback for non-paginated response (shouldn't happen)
        setAssignments(response as Assignment[]);
        setTotalPages(1);
        setTotalCount(response.length);
      } else {
        showToast("Failed to load assignments", "error", true);
        setAssignments([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load assignments";
      showToast(errorMessage, "error", true);
      setAssignments([]);
    } finally {
      isFirstFetch.current = false;
      setInitialLoading(false);
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };


  const handleViewAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setViewDialogOpen(true);
  };


  const handleDownloadReport = async (): Promise<void> => {
    setDownloadLoading(true);
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();
      
      if (employeeFilter !== "all") {
        params.append("employee_code", employeeFilter);
      }
      if (designationFilter !== "all") {
        params.append("designation", designationFilter);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (templateFilter !== "all") {
        params.append("template", templateFilter);
      }
      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }

      const url = `${generateKRAReports}?${params.toString()}`;

      // Get token (same key as Redux actions)
      const token = localStorage.getItem("token");
      if (!token) {
        showToast("Authentication required. Please login again.", "error", true);
        return;
      }

      // Fetch as blob
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = "Failed to generate report";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Download file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `KRA_Assignments_Report_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showToast("Report downloaded successfully", "success", true);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to download report";
      showToast(errorMessage, "error", true);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleClearFilters = () => {
    setEmployeeFilter("all");
    setDesignationFilter("all");
    setStatusFilter("all");
    setTemplateFilter("all");
    setSearchInput("");
    setSearchQuery("");
    setSortField("");
    setSortOrder("asc");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    employeeFilter !== "all" ||
    designationFilter !== "all" ||
    statusFilter !== "all" ||
    templateFilter !== "all" ||
    searchInput.trim() !== "" ||
    sortField !== "";

  // Debounce search input: update searchQuery 500ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to page 1 whenever filters or search change (but not when currentPage itself changes)
  const isFilterChange = useRef(false);
  useEffect(() => {
    isFilterChange.current = true;
    setCurrentPage(1);
  }, [searchQuery, employeeFilter, designationFilter, statusFilter, templateFilter, sortField, sortOrder]);

  // Fetch whenever pagination or filters change
  useEffect(() => {
    // If a filter change just reset the page to 1, skip the fetch here;
    // the page state update will immediately cause another run with currentPage === 1.
    if (isFilterChange.current && currentPage !== 1) {
      isFilterChange.current = false;
      return;
    }
    isFilterChange.current = false;
    fetchAssignments();
  }, [currentPage, pageSize, searchQuery, employeeFilter, designationFilter, statusFilter, templateFilter, sortField, sortOrder]);

  if (initialLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>KRA Assignments</PageTitle>
        </Box>
        <Typography variant="body1" color="black">
          Track and monitor all KRA/KPI assignments across the organization
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Total Assignments
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Pending Acceptance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "warning.main" }}>
                {statusCounts.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Accepted
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "info.main" }}>
                {statusCounts.accepted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Completed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                {statusCounts.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={employeeFilter}
                label="Employee"
                onChange={(e) => setEmployeeFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="all">All Employees</MenuItem>
                {filterOptions.employees.map((employee) => (
                  <MenuItem key={employee.employee_code} value={employee.employee_code}>
                    {employee.employee_name} ({employee.employee_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={designationFilter}
                label="Role"
                onChange={(e) => setDesignationFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {filterOptions.designations.map((designation) => (
                  <MenuItem key={designation} value={designation}>
                    {designation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="awaiting_manager_review">Awaiting Manager Review</MenuItem>
                <MenuItem value="awaiting_hr_review">Awaiting HR Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>KRA</InputLabel>
              <Select
                value={templateFilter}
                label="KRA"
                onChange={(e) => setTemplateFilter(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="all">All KRAs</MenuItem>
                {filterOptions.templates.map((template) => (
                  <MenuItem key={template} value={template}>
                    {template}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Quick search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Grid>
        </Grid>
        
        {/* Download and Clear Filters */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="black" sx={{ fontStyle: "italic" }}>
            📊 Report includes all KPIs with ratings and comments based on selected filters
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              sx={{
                textTransform: "none",
                borderColor: "error.main",
                color: "error.main",
                "&:hover:not(:disabled)": {
                  borderColor: "error.main",
                  color: "error.main",
                  boxShadow: "0 0 12px rgba(211, 47, 47, 0.5)",
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
                "&:active:not(:disabled)": {
                  boxShadow: "0 0 16px rgba(211, 47, 47, 0.6)",
                },
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={downloadLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={downloadLoading || assignments.length === 0}
            >
              {downloadLoading ? "Generating..." : "Download Report"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Assignments Table */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}
      {!loading && assignments.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <AssignmentTurnedInIcon sx={{ fontSize: 80, color: "black", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Assignments Found
          </Typography>
          <Typography variant="body2" color="black">
            {totalCount === 0
              ? "No KRA assignments have been made yet."
              : "No assignments match your search criteria."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "employee__first_name"}
                    direction={sortField === "employee__first_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("employee__first_name")}
                  >
                    Employee
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "employee__designation__designation_name"}
                    direction={sortField === "employee__designation__designation_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("employee__designation__designation_name")}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "template__form_name"}
                    direction={sortField === "template__form_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("template__form_name")}
                  >
                    KRA
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">Assigned Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "template__review_period"}
                    direction={sortField === "template__review_period" ? sortOrder : "asc"}
                    onClick={() => handleSort("template__review_period")}
                  >
                    Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 75 }} align="center">
                  <TableSortLabel
                    active={sortField === "hr_overall_rating"}
                    direction={sortField === "hr_overall_rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("hr_overall_rating")}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 70 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "600" }}>
                        {assignment.employee_name}
                      </Typography>
                      <Typography variant="caption" color="black">
                        {assignment.employee_code}
                      </Typography>
                      {/* M1 / M2 manager + project chips */}
                      {(assignment.manager_1_name || assignment.manager_2_name) && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {assignment.manager_1_name && (
                            <Chip
                              label={
                                assignment.m1_project_name
                                  ? `M1 · ${assignment.m1_project_name}`
                                  : `M1 · ${assignment.manager_1_name}`
                              }
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.65rem", height: 18 }}
                            />
                          )}
                          {assignment.manager_2_name && (
                            <Chip
                              label={
                                assignment.m2_project_name
                                  ? `M2 · ${assignment.m2_project_name}`
                                  : `M2 · ${assignment.manager_2_name}`
                              }
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: "0.65rem", height: 18 }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{assignment.designation}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{assignment.form_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography variant="body2" color="black">
                        {formatDate(assignment.assigned_date)}
                      </Typography>
                      {assignment.accepted_date && (
                        <Typography variant="body2" color="success.main">
                          Accepted: {formatDate(assignment.accepted_date)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip 
                          label={getPeriodShortLabel(assignment.review_period)} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                        />
                        <Typography variant="body2" color="black">
                          End: {formatDate(assignment.period_end_date)}
                        </Typography>
                      </Box>
                      {assignment.days_remaining > 0 && !assignment.is_period_expired && (
                        <Chip 
                          label={`${assignment.days_remaining}d`}
                          size="small"
                          color={getPeriodDaysWarningColor(assignment.days_remaining)}
                        />
                      )}
                      {assignment.is_period_expired && (
                        <Chip label="Expired" size="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(assignment.status)}</TableCell>
                  <TableCell align="center" sx={{ width: 75 }}>
                    {assignment.hr_overall_rating ? (
                      <Chip
                        label={`${assignment.hr_overall_rating.toFixed(1)}/5`}
                        size="small"
                        color="primary"
                      />
                    ) : (
                      <Typography variant="body2" color="black">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 70 }}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleViewAssignment(assignment)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      {!loading && !initialLoading && assignments.length > 0 && (
        <StandardTablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          onPageChange={(_: unknown, newPage: number) => setCurrentPage(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
        />
      )}

      {/* View Assignment Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {selectedAssignment?.employee_name} - {selectedAssignment?.form_name}
            </Typography>
            <Typography variant="body2" color="black">
              {selectedAssignment?.designation}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAssignment && (
            <Box>
              {/* Assignment Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="black">
                    Employee Code
                  </Typography>
                  <Typography variant="body2">{selectedAssignment.employee_code}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="black">
                    Email
                  </Typography>
                  <Typography variant="body2">{selectedAssignment.employee_email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="black">
                    Assigned Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedAssignment.assigned_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="black">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedAssignment.status)}</Box>
                </Grid>
                {selectedAssignment.accepted_date && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Accepted Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedAssignment.accepted_date)}
                    </Typography>
                  </Grid>
                )}
                {selectedAssignment.self_review_date && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Self Review Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedAssignment.self_review_date)}
                    </Typography>
                  </Grid>
                )}
                {selectedAssignment.manager_review_date && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Manager Review Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(selectedAssignment.manager_review_date)}
                    </Typography>
                  </Grid>
                )}
                {selectedAssignment.self_review_overall_rating && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Self Rating
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "default.main" }}>
                      {selectedAssignment.self_review_overall_rating.toFixed(1)}/5
                    </Typography>
                  </Grid>
                )}
                {/* Show M1 and M2 ratings separately when dual managers exist */}
                {selectedAssignment.manager_1_overall_rating != null ? (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      {selectedAssignment.manager_1_name
                        ? selectedAssignment.m1_project_name
                          ? `${selectedAssignment.manager_1_name} · ${selectedAssignment.m1_project_name} (M1) Rating`
                          : `${selectedAssignment.manager_1_name} (M1) Rating`
                        : "M1 Rating"}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "success.main" }}>
                      {selectedAssignment.manager_1_overall_rating.toFixed(1)}/5
                    </Typography>
                  </Grid>
                ) : selectedAssignment.manager_overall_rating ? (
                  // Legacy single-manager fallback
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Manager Rating
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "secondary.main" }}>
                      {selectedAssignment.manager_overall_rating.toFixed(1)}/5
                    </Typography>
                  </Grid>
                ) : null}
                {selectedAssignment.manager_2_overall_rating != null && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      {selectedAssignment.manager_2_name
                        ? selectedAssignment.m2_project_name
                          ? `${selectedAssignment.manager_2_name} · ${selectedAssignment.m2_project_name} (M2) Rating`
                          : `${selectedAssignment.manager_2_name} (M2) Rating`
                        : "M2 Rating"}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "secondary.main" }}>
                      {selectedAssignment.manager_2_overall_rating.toFixed(1)}/5
                    </Typography>
                  </Grid>
                )}
                {selectedAssignment.hr_overall_rating && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      HR Rating
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                      {selectedAssignment.hr_overall_rating.toFixed(1)}/5
                    </Typography>
                  </Grid>
                )}
                {selectedAssignment.final_grade && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="black">
                      Final Grade
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                      {selectedAssignment.final_grade}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* KRA Sections */}
              <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 2 }}>
                KRA Sections
              </Typography>
              {selectedAssignment.sections.map((section, sectionIndex) => (
                <Accordion key={sectionIndex} elevation={0} sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: "grey.50" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "600" }}>
                        {sectionIndex + 1}. {section.section}
                      </Typography>
                      <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                        {section.weightage}%
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ pl: 2 }}>
                      {section.questions.map((kpi, kpiIndex) => (
                        <Box
                          key={kpiIndex}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 1.5,
                            borderBottom:
                              kpiIndex < section.questions.length - 1
                                ? "1px solid"
                                : "none",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                            • {kpi.question}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "success.main",
                              fontWeight: "600",
                              ml: 2,
                              minWidth: "50px",
                              textAlign: "right",
                            }}
                          >
                            {kpi.weightage}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KRAAssignments;
